import React, { useState } from "react";
import {
  Box,
  IconButton,
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  Tooltip,
  Link,
} from "@mui/material";
import { Fullscreen, Close, GetApp } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface Props {
  fileUrl: string;
  fileName?: string;
}

export function DocumentViewer({ fileUrl, fileName }: Props) {
  const [t] = useTranslation();
  const [fullscreen, setFullscreen] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const isPdf =
    fileUrl.toLowerCase().includes(".pdf") ||
    fileUrl.toLowerCase().includes("content-type=application%2fpdf") ||
    fileUrl.toLowerCase().includes("content-type=application/pdf");

  if (!isPdf) {
    return (
      <Box
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <GetApp color="primary" />
        {/* Theme-aware link — the browser-default blue was unreadable on
            the dark background (FL-281). */}
        <Link
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          color="text.primary"
          underline="always"
        >
          {fileName || String(t("Download attached file"))}
        </Link>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
            display: "flex",
            gap: 0.5,
          }}
        >
          <Tooltip title={String(t("Download"))}>
            <IconButton
              component="a"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{ bgcolor: "background.paper", opacity: 0.8 }}
            >
              <GetApp fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={String(t("Fullscreen"))}>
            <IconButton
              onClick={() => setFullscreen(true)}
              size="small"
              sx={{ bgcolor: "background.paper", opacity: 0.8 }}
            >
              <Fullscreen fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {loadError ? (
          <Box
            sx={{
              height: "calc(100vh - 300px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                {t("Unable to preview this file.")}
              </Typography>
              <Link
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                color="text.primary"
                underline="always"
              >
                {t("Download instead")}
              </Link>
            </Box>
          </Box>
        ) : (
          <iframe
            src={fileUrl}
            title={fileName || String(t("Document"))}
            onError={() => setLoadError(true)}
            style={{
              width: "100%",
              height: "calc(100vh - 300px)",
              border: "1px solid #555",
              borderRadius: 4,
            }}
          />
        )}
      </Box>

      <Dialog open={fullscreen} onClose={() => setFullscreen(false)} fullScreen>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setFullscreen(false)}
              aria-label={String(t("Close"))}
            >
              <Close />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {fileName || String(t("Document Preview"))}
            </Typography>
            <IconButton
              color="inherit"
              component="a"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={String(t("Download"))}
            >
              <GetApp />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, bgcolor: "#000" }}>
          <iframe
            src={fileUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title={fileName || String(t("Document Fullscreen Preview"))}
          />
        </Box>
      </Dialog>
    </>
  );
}
